import { useState } from 'react';
import { PlusIcon, EyeIcon, EyeSlashIcon, ClipboardIcon, DocumentDuplicateIcon, TrashIcon } from '@heroicons/react/24/solid';
import { useWheel } from '../context/WheelContext';
import RouletteWheel from '../components/RouletteWheel';
import Confetti from '../components/Confetti';
import Footer from '../components/Footer';
import WinnersHistory from '../components/WinnersHistory';
import Header from '../components/Header';
import TabNavigation from '../components/TabNavigation';

function Wheel() {
  const { 
    participants, 
    participantsList,
    addParticipant,
    toggleParticipant,
    removeParticipant,
    addWinner,
    clearParticipants,
    isSharkMode
  } = useWheel();
  const [newParticipant, setNewParticipant] = useState('');
  const [winner, setWinner] = useState(null);
  const [isSpinning, setIsSpinning] = useState(false);
  const [error, setError] = useState('');
  const [showPasteModal, setShowPasteModal] = useState(false);
  const [pasteContent, setPasteContent] = useState('');

  const handleAddParticipant = (e) => {
    e.preventDefault();
    if (newParticipant.trim()) {
      try {
        addParticipant(newParticipant.trim());
        setNewParticipant('');
        setError('');
      } catch (err) {
        setError(err.message);
      }
    }
  };

  const handlePasteSubmit = (e) => {
    e.preventDefault();
    const names = pasteContent
      .split('\n')
      .map(name => name.trim())
      .filter(name => name.length > 0);

    let addedCount = 0;
    let duplicateCount = 0;

    names.forEach(name => {
      try {
        addParticipant(name);
        addedCount++;
      } catch (err) {
        duplicateCount++;
      }
    });

    setPasteContent('');
    setShowPasteModal(false);
    setError(
      duplicateCount > 0 
        ? `Se agregaron ${addedCount} participantes. ${duplicateCount} nombres duplicados fueron omitidos.`
        : `Se agregaron ${addedCount} participantes exitosamente.`
    );
  };

  const spinWheel = () => {
    if (participants.length < 2) return;
    setIsSpinning(true);
    setWinner(null);
  };

  const handleSpinEnd = () => {
    setIsSpinning(false);
  };

  const handleSelectWinner = (selectedWinner) => {
    setWinner(selectedWinner);
    addWinner(selectedWinner);
  };

  const handleCopyParticipants = () => {
    const participantsList = participants.join('\n');
    navigator.clipboard.writeText(participantsList).then(() => {
      setError('Lista copiada al portapapeles');
      // Limpiar el mensaje después de 2 segundos
      setTimeout(() => setError(''), 2000);
    });
  };

  const handleClearList = () => {
    if (window.confirm('¿Estás seguro de que quieres eliminar todos los participantes?')) {
      clearParticipants();
      setWinner(null);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header con z-index más alto */}
      <div className="relative z-header">
        <Header />
      </div>

      {/* Contenedor principal con fondo relativo */}
      <div className="flex-grow relative">
        {/* Fondo animado del modo tiburón - limitado al contenedor principal */}
        {isSharkMode && (
          <div className="absolute inset-0 overflow-hidden">
            {/* Fondo de mar */}
            <div className="absolute inset-0 bg-gradient-to-b from-blue-400 to-blue-900 opacity-20" />
            
            {/* Tiburones nadando en diferentes direcciones y tamaños */}
            {[...Array(8)].map((_, i) => (
              <div
                key={`shark-${i}`}
                className="absolute text-4xl animate-swim opacity-30"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDuration: `${15 + Math.random() * 10}s`,
                  animationDelay: `${Math.random() * 5}s`,
                  transform: i % 2 === 0 ? 'scaleX(-1)' : '',
                  fontSize: `${2 + Math.random() * 2}rem`,
                }}
              >
                🦈
              </div>
            ))}

            {/* Dólares flotando */}
            {[...Array(12)].map((_, i) => (
              <div
                key={`money-${i}`}
                className="absolute text-3xl animate-float-money opacity-30"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDuration: `${8 + Math.random() * 7}s`,
                  animationDelay: `${Math.random() * 5}s`,
                  transform: `rotate(${Math.random() * 360}deg)`,
                  fontSize: `${1.5 + Math.random() * 1}rem`,
                }}
              >
                {['💵', '💰', '💎', '💸'][Math.floor(Math.random() * 4)]}
              </div>
            ))}

            {/* Burbujas para efecto submarino */}
            {[...Array(20)].map((_, i) => (
              <div
                key={`bubble-${i}`}
                className="absolute text-2xl animate-float opacity-20"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDuration: `${3 + Math.random() * 4}s`,
                  animationDelay: `${Math.random() * 2}s`,
                  fontSize: `${0.5 + Math.random() * 1}rem`,
                }}
              >
                🫧
              </div>
            ))}
          </div>
        )}

        {/* Contenido principal sobre el fondo */}
        <div className="relative h-full">
          <TabNavigation>
            {(activeTab) => (
              <div className="p-6">
                {winner && <Confetti />}
                <div className="max-w-7xl mx-auto">
                  {activeTab === 'wheel' ? (
                    <div className="flex flex-col lg:flex-row gap-8">
                      {/* Panel izquierdo */}
                      <div className="lg:w-1/3 rounded-xl shadow-lg p-6 border border-secondary-200 bg-white relative isolate">
                        <div className="absolute inset-0 bg-white rounded-xl -z-10"></div>
                        <h2 className="text-2xl font-bold text-secondary-900 flex items-center gap-2">
                          {isSharkMode ? '🦈 Participantes' : '👥 Participantes'}
                        </h2>
                        
                        {/* Formulario */}
                        <div className="mb-6">
                          <form onSubmit={handleAddParticipant} className="flex flex-col gap-2">
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={newParticipant}
                                onChange={(e) => setNewParticipant(e.target.value)}
                                className="flex-1 px-4 py-2 border border-secondary-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                placeholder="Nombre del participante"
                              />
                              <button
                                type="submit"
                                className="bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors"
                              >
                                <PlusIcon className="h-6 w-6" />
                              </button>
                            </div>
                            {error && (
                              <p className={`text-sm mt-1 animate-fadeIn ${
                                error.includes('copiada') ? 'text-green-500' : 'text-red-500'
                              }`}>
                                {error}
                              </p>
                            )}
                          </form>
                          
                          <div className="flex gap-2 mt-4">
                            <button
                              onClick={() => setShowPasteModal(true)}
                              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-secondary-700 bg-secondary-100 rounded-lg hover:bg-secondary-200 transition-colors"
                            >
                              <ClipboardIcon className="h-5 w-5" />
                              <span>Pegar lista</span>
                            </button>
                            <button
                              onClick={handleCopyParticipants}
                              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-secondary-700 bg-secondary-100 rounded-lg hover:bg-secondary-200 transition-colors"
                            >
                              <DocumentDuplicateIcon className="h-5 w-5" />
                              <span>Copiar lista</span>
                            </button>
                          </div>
                        </div>

                        {/* Lista de participantes */}
                        <div className="overflow-y-auto max-h-[calc(100vh-300px)]">
                          <ul className="space-y-2">
                            {participantsList.map((participant) => (
                              <li
                                key={participant.id}
                                className={`
                                  px-4 py-3 rounded-lg flex justify-between items-center
                                  transition-colors duration-200
                                  ${participant.active 
                                    ? 'bg-secondary-50 hover:bg-secondary-100' 
                                    : 'bg-secondary-200'}
                                `}
                              >
                                <span className={`font-medium ${!participant.active && 'text-gray-500 line-through'}`}>
                                  {participant.name}
                                </span>
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => toggleParticipant(participant.id)}
                                    className={`p-1.5 rounded-lg transition-colors
                                      ${participant.active 
                                        ? 'text-blue-500 hover:bg-blue-50' 
                                        : 'text-gray-500 hover:bg-gray-300'
                                      }`}
                                    title={participant.active ? 'Deshabilitar' : 'Habilitar'}
                                  >
                                    {participant.active ? (
                                      <EyeIcon className="h-5 w-5" />
                                    ) : (
                                      <EyeSlashIcon className="h-5 w-5" />
                                    )}
                                  </button>
                                  <button
                                    onClick={() => removeParticipant(participant.id)}
                                    className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Eliminar"
                                  >
                                    ×
                                  </button>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Contador */}
                        <div className="mt-4 text-sm text-gray-600">
                          {isSharkMode ? '🎯 Participantes activos: ' : '👥 Participantes activos: '}
                          {participants.length} de {participantsList.length}
                        </div>
                      </div>

                      {/* Panel derecho */}
                      <div className="lg:w-2/3 rounded-xl shadow-lg p-6 border border-secondary-200 bg-white relative isolate flex flex-col items-center justify-center">
                        <div className="absolute inset-0 bg-white rounded-xl -z-10"></div>
                        <h1 className="text-3xl font-bold text-center mb-8 text-secondary-900">
                          {isSharkMode 
                            ? '🦈 Shark Roulette' 
                            : '🎯 Ruleta Aleatoria'}
                        </h1>
                        
                        {participants.length >= 2 ? (
                          <>
                            <div className="mb-8">
                              <RouletteWheel
                                participants={participants}
                                spinning={isSpinning}
                                onSpinEnd={handleSpinEnd}
                                onSelectWinner={handleSelectWinner}
                                winner={winner}
                              />
                            </div>

                            <button
                              onClick={spinWheel}
                              disabled={isSpinning}
                              className={`
                                px-8 py-4 rounded-full text-white font-semibold text-lg
                                transition-all transform hover:scale-105
                                ${isSpinning
                                  ? 'bg-gray-400 cursor-not-allowed'
                                  : isSharkMode
                                    ? 'bg-blue-600 hover:bg-blue-700 active:scale-95'
                                    : 'bg-green-500 hover:bg-green-600 active:scale-95'}
                              `}
                            >
                              {isSpinning ? 'Girando...' : isSharkMode ? '🦈 Girar Ruleta' : '🎯 Girar Ruleta'}
                            </button>

                            {winner && (
                              <div className="mt-8 text-center">
                                <div className={`
                                  animate-bounce-in p-6 rounded-xl shadow-lg border-2
                                  ${isSharkMode 
                                    ? 'border-blue-500 bg-blue-50' 
                                    : 'border-success-500 bg-white'}
                                `}>
                                  <h2 className={`
                                    text-4xl font-bold mb-4 animate-scale-in
                                    ${isSharkMode ? 'text-blue-600' : 'text-success-600'}
                                  `}>
                                    {isSharkMode ? '🦈 ¡Participante Seleccionado!' : '🎉 ¡Felicitaciones!'}
                                  </h2>
                                  <div className={`
                                    text-3xl font-bold p-4 rounded-lg
                                    ${isSharkMode 
                                      ? 'text-blue-500 bg-blue-100' 
                                      : 'text-success-500 bg-success-50'}
                                  `}>
                                    {winner}
                                  </div>
                                  <div className={`
                                    mt-4 animate-fadeIn
                                    ${isSharkMode ? 'text-blue-600' : 'text-secondary-600'}
                                  `}>
                                    {isSharkMode ? '🎯 Prepárate para tu presentación' : '🏆 Ha sido seleccionado como ganador'}
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="text-center text-gray-500">
                            <p className="text-xl mb-2">
                              {isSharkMode 
                                ? '🦈 Agrega al menos 2 participantes' 
                                : '👥 Agrega al menos 2 participantes'}
                            </p>
                            <p>para comenzar {isSharkMode ? 'el Shark Tank' : 'a jugar'}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-white rounded-xl shadow-lg relative isolate">
                      <div className="absolute inset-0 bg-white rounded-xl -z-10"></div>
                      <WinnersHistory />
                    </div>
                  )}
                </div>
              </div>
            )}
          </TabNavigation>
        </div>
      </div>

      {/* Footer sobre el fondo */}
      <div className="relative z-header bg-white">
        <Footer />
      </div>

      {/* Modal */}
      {showPasteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-modal">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4 text-gray-800">Pegar lista de nombres</h3>
            <p className="text-gray-600 mb-4">
              Pega una lista de nombres (uno por línea)
            </p>
            <form onSubmit={handlePasteSubmit}>
              <textarea
                value={pasteContent}
                onChange={(e) => setPasteContent(e.target.value)}
                className="w-full h-40 p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Juan Pérez&#10;María García&#10;Carlos López"
              />
              <div className="flex gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => setShowPasteModal(false)}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition-colors"
                >
                  Agregar nombres
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Wheel; 